import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Vote, Lightbulb, DollarSign, HandCoins, Smartphone } from 'lucide-react';
import { cn } from '@/lib/shared/utils';

interface FeatureTab {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  features: Array<{
    title: string;
    description: string;
    impact: string;
    isUnique: boolean;
  }>;
  callout: string;
}

const featureTabs: FeatureTab[] = [
  {
    id: 'voting',
    title: 'Transparent Democratic Process',
    subtitle: 'Real democracy, not politics',
    icon: Vote,
    callout:
      'A transparent voting system designed specifically for communities, with per-voter receipts and a complete audit trail',
    features: [
      {
        title: 'Verifiable Voting',
        description:
          'Every vote produces a receipt you can check yourself, and a tally anyone can recount',
        impact: 'End voting disputes and build trust in community decisions',
        isUnique: true,
      },
      {
        title: 'Multi-Ballot Democracy',
        description:
          'Handle everything from simple polls to complex budget votes with multiple voting types',
        impact: 'More nuanced decision-making that reflects community complexity',
        isUnique: false,
      },
      {
        title: 'Real-Time Transparency',
        description: 'Live voting results, participation tracking, and complete audit trails',
        impact: 'No more waiting weeks for results or questioning vote integrity',
        isUnique: false,
      },
      {
        title: 'One Vote Per Parcel',
        description:
          'Enforced one-vote-per-parcel counting with configurable pass and fail thresholds set by your governing documents',
        impact: 'Election results that match your bylaws, every time',
        isUnique: false,
      },
    ],
  },
  {
    id: 'community-connection',
    title: 'Stay Connected, Stay Informed',
    subtitle: 'Records, messages, and meetings in one place',
    icon: Smartphone,
    callout:
      'A community platform built to replace scattered email chains and paper notices with one shared, searchable record',
    features: [
      {
        title: 'Direct Messaging',
        description:
          'Message the board and departments directly, with emergency alerts when needed',
        impact: 'Reach the right people without hunting for the right email address',
        isUnique: false,
      },
      {
        title: 'Meeting Records',
        description:
          'Meeting recordings and minutes archived and searchable by every landowner, alongside project documents and decisions',
        impact: 'No more digging through old emails to find what was decided',
        isUnique: true,
      },
      {
        title: 'Event Calendar',
        description:
          'A real calendar view of community events with ICS export to your own calendar app',
        impact: 'Know what is happening without cross-checking three different lists',
        isUnique: false,
      },
      {
        title: 'Works on Any Device',
        description:
          'Responsive web access from a phone, tablet, or computer, no app download required',
        impact: 'Everyone in the community can use it, regardless of what device they own',
        isUnique: false,
      },
    ],
  },
];

interface RevolutionaryFeaturesProps {
  onScheduleDemo?: () => void;
  onSeeLiveDemo?: () => void;
}

export function RevolutionaryFeatures({
  onScheduleDemo,
  onSeeLiveDemo,
}: RevolutionaryFeaturesProps = {}) {
  const [activeTab, setActiveTab] = useState(featureTabs[0].id);
  const activeFeature = featureTabs.find((tab) => tab.id === activeTab) || featureTabs[0];

  return (
    <section className="py-20 bg-linear-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-purple-500/20 text-purple-200 border-purple-400">
            Built for Communities
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Features Built Specifically
            <span className="block text-purple-400">for How Communities Actually Work</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-8">
            We're not just another HOA app. Here's what makes this platform a fit for
            forward-thinking communities.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {featureTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all duration-300',
                  'border-2 backdrop-blur-xs',
                  activeTab === tab.id
                    ? 'bg-purple-500/30 border-purple-400 text-white shadow-lg shadow-purple-500/25'
                    : 'bg-slate-800/50 border-slate-600 text-gray-300 hover:bg-slate-700/50 hover:border-slate-500',
                )}
              >
                <Icon className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">{tab.title}</div>
                  <div className="text-xs opacity-75">{tab.subtitle}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active Tab Content */}
        <div className="max-w-6xl mx-auto">
          {/* Callout Banner */}
          <div className="bg-linear-to-r from-purple-500/20 to-blue-500/20 rounded-2xl p-6 mb-8 border border-purple-400/30">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500/30 rounded-full flex items-center justify-center">
                <Lightbulb className="w-6 h-6 text-purple-300" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1">How This Helps</h3>
                <p className="text-purple-200">{activeFeature.callout}</p>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {activeFeature.features.map((feature, index) => (
              <Card
                key={index}
                className="bg-slate-800/50 border-slate-600 backdrop-blur-xs hover:bg-slate-700/50 transition-all duration-300"
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 bg-linear-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center shrink-0">
                      <span className="text-white font-bold text-sm">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-lg font-bold text-white">{feature.title}</h4>
                        {feature.isUnique && (
                          <Badge className="bg-green-500/20 text-green-300 border-green-400 text-xs">
                            UNIQUE
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-300 mb-3">{feature.description}</p>
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="w-4 h-4 text-green-400" />
                        <span className="text-green-300 font-medium">{feature.impact}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-linear-to-r from-slate-800/50 to-purple-800/50 rounded-2xl p-8 border border-purple-400/30 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Transform Your Community?
            </h3>
            <p className="text-gray-300 mb-6">These features are included in every package.</p>
            <div className="flex flex-col gap-4 justify-center items-center">
              <Button
                size="lg"
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={onSeeLiveDemo}
              >
                <HandCoins className="w-5 h-5 mr-2" />
                Schedule Live Demo
              </Button>
              <p className="text-gray-400 text-sm">
                45-60 minutes to understand your complete vision
              </p>
              {/*
              <Button 
                size="lg" 
                variant="outline" 
                className="border-purple-400 text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 hover:text-white transition-all duration-300"
                onClick={onScheduleDemo}
              >
                Schedule Deep Dive
              </Button>
              */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
